import {
  Fade,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { useMemo, useState } from 'react';
import { COLORS } from '../../theme/variables';
import { CLASS_TRACKING } from '../../utils/tracking_class';

interface TableAction {
  key: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  hidden?: boolean;
  onClick: () => void;
}

interface TableActionsProps {
  actions: TableAction[];
  variant?: 'table' | 'list';
  disabled?: boolean;
  tooltip?: string;
}

const useStyles = makeStyles(() => ({
  menuItem: {
    gap: 8,
  },
  addBtn: {
    padding: 4,
    background: COLORS.COLOR_BLUE_BASE,
    color: 'white',

    '&:hover': {
      color: 'white',
      background: COLORS.COLOR_BLUE_LIGHTENED_5,
      boxShadow:
        '0px 2px 4px rgba(34, 91, 187, 0.2), 0px 4px 8px rgba(51, 126, 255, 0.12)',
    },
  },
}));

function TableActions({
  actions,
  variant = 'table',
  disabled,
  tooltip = '',
}: TableActionsProps) {
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const activeActions = useMemo(
    () => actions.filter((a) => !a.hidden),
    [actions],
  );

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return activeActions?.length ? (
    <>
      {(() => {
        switch (variant) {
          case 'list':
            return (
              <Tooltip title={tooltip}>
                <div>
                  <IconButton
                    data-testid='table-action-button'
                    className={classes.addBtn}
                    onClick={handleOpen}
                    disabled={disabled}>
                    <AddIcon />
                  </IconButton>
                </div>
              </Tooltip>
            );
          case 'table':
          default:
            return (
              <Tooltip title={tooltip}>
                <div>
                  <IconButton
                    data-testid='table-action-button'
                    aria-describedby={id}
                    className={CLASS_TRACKING.LIST_QUICK_ACTION}
                    onClick={handleOpen}
                    disabled={disabled}>
                    <MoreHorizIcon />
                  </IconButton>
                </div>
              </Tooltip>
            );
        }
      })()}
      <Menu
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        keepMounted
        TransitionComponent={Fade}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}>
        {activeActions.map((action) => (
          <MenuItem
            disabled={action.disabled}
            onClick={() => {
              action.onClick();
              handleClose();
            }}
            className={classes.menuItem}
            key={action.key}>
            {action.icon}
            <span>{action.label}</span>
          </MenuItem>
        ))}
      </Menu>
    </>
  ) : null;
}

export default TableActions;
